import { MessageCircle, Bot, User } from 'lucide-react';
import React, { useState, useEffect, useRef } from 'react';
import '../../App.css';
import './chatbox.css';
import API_ENDPOINTS from '../../Config/api';
import { fetchWithAuth } from '../../Utils/fetchWithAuth';

const QUERY_MAX_LENGTH = 2000;

const PRIVACY_NOTICE =
  'Per buona norma evita di inserire dati personali: le conversazioni restano al sicuro all\'interno del nostro sistema.';

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sessionId, setSessionId] = useState(null);
  const [loading, setLoading] = useState(false);
  const endRef = useRef(null);
  const textareaRef = useRef(null);

  useEffect(() => {
    if (endRef.current) endRef.current.scrollIntoView({ behavior: 'smooth' });
  }, [messages, open]);

  useEffect(() => {
    if (open && textareaRef.current) {
      setTimeout(() => textareaRef.current.focus(), 120);
    }
  }, [open]);

  const sendMessage = async () => {
    if (!input || loading) return;
    const q = input.trim();
    if (!q) return;

    if (q.length > QUERY_MAX_LENGTH) {
      setMessages((m) => [
        ...m,
        {
          role: 'assistant',
          content: `Messaggio troppo lungo (max ${QUERY_MAX_LENGTH} caratteri). Accorcia la domanda.`,
        },
      ]);
      return;
    }

    setInput('');
    const userMsg = { role: 'user', content: q };
    setMessages((m) => [...m, userMsg]);
    setLoading(true);

    const body = { query: q, session_id: sessionId, use_rag: true, contexts: 'asight' };
    try {
      const res = await fetchWithAuth(API_ENDPOINTS.chatboxMessage, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      if (!res) throw new Error('Auth failed');
      if (!res.ok) {
        let errMsg = 'Errore nella richiesta. Riprova.';
        try {
          const errJson = await res.json();
          errMsg = errJson.detail
            || (Array.isArray(errJson.query) ? errJson.query[0] : null)
            || errMsg;
        } catch {
          const text = await res.text();
          if (text) errMsg = text.slice(0, 500);
        }
        setMessages((m) => [...m, { role: 'assistant', content: errMsg }]);
        return;
      }

      const data = await res.json();
      setSessionId(data.session_id || sessionId);
      const assistantMsg = { role: 'assistant', content: data.answer || '—' };
      setMessages((m) => [...m, assistantMsg]);
    } catch (err) {
      setMessages((m) => [...m, { role: 'assistant', content: 'Errore nella richiesta. Riprova.' }]);
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <div className={`chatbox-root ${open ? 'open' : ''}`}>
      <div className="chatbox-button" onClick={() => setOpen(!open)} aria-label={open ? 'Chiudi chat' : 'Apri chat'}>
        <span className="chatbox-icon" aria-hidden>
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
            <rect x="1" y="1" width="22" height="22" rx="6" fill="currentColor" opacity="0.06" />
            <path d="M7 8.5c0-1.3807 1.1193-2.5 2.5-2.5h5c1.3807 0 2.5 1.1193 2.5 2.5v3c0 1.3807-1.1193 2.5-2.5 2.5h-3L9 17v-2H8.5C7.1193 15 7 13.8807 7 12.5v-4z" fill="white" />
          </svg>
        </span>
      </div>

      {open && (
        <div className="chatbox-window">
          <div className="chatbox-header">
            <div className="chatbox-header-left">
              <strong>Assistente</strong>
              <div className="chatbox-header-sub">Domande su come usare A-Sight</div>
            </div>
            <button className="chatbox-close" onClick={() => setOpen(false)} aria-label="Chiudi chat">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
                <path d="M6 6 L18 18 M6 18 L18 6" stroke="#102022" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
          </div>
          <div className="chatbox-privacy-notice" role="note">
            {PRIVACY_NOTICE}
          </div>
          <div className="chatbox-body">
            {messages.length === 0 ? (
              <div className="chatbox-welcome">
                <div className="chatbox-welcome-icon"><MessageCircle size={24} /></div>
                <div className="chatbox-welcome-text">
                  <div className="chatbox-welcome-title">Hai bisogno di aiuto?</div>
                  <div className="chatbox-welcome-desc">Chiedi come usare menu, sezioni e funzioni. Prova con:</div>
                  <div className="chatbox-welcome-examples">
                    {['Cosa trovo nella Dashboard?', 'Come verifico la conformità NIS2?', 'Come funziona la Cyber Surveillance?'].map((ex) => (
                      <button
                        key={ex}
                        className="chatbox-example"
                        onClick={() => { setInput(ex); if (textareaRef.current) textareaRef.current.focus(); }}
                        type="button"
                      >
                        {ex}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              messages.map((m, idx) => (
                <div key={idx} className={`chatbox-message ${m.role}`}>
                  <div className="chatbox-message-avatar">{m.role === 'assistant' ? <Bot size={16} className="inline" /> : <User size={16} className="inline" />}</div>
                  <div className="chatbox-message-content">{m.content}</div>
                </div>
              ))
            )}

            {loading && (
              <div className="chatbox-message assistant typing">
                <div className="chatbox-message-avatar"><Bot size={16} /></div>
                <div className="chatbox-message-content chatbox-typing">
                  <div className="typing-dots" aria-hidden>
                    <span></span><span></span><span></span>
                  </div>
                </div>
              </div>
            )}
            <div ref={endRef} />
          </div>
          <div className="chatbox-input">
            <textarea
              ref={textareaRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={onKeyDown}
              maxLength={QUERY_MAX_LENGTH}
              placeholder="Es. Cosa trovo in I miei Asset?"
              aria-label="Messaggio chat"
            />
            <button onClick={sendMessage} disabled={loading || !input.trim()}>Invia</button>
          </div>
        </div>
      )}
    </div>
  );
}
